import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumb, Row, Col, PageHeader, Skeleton } from 'antd';
import { BlockOutlined } from '@ant-design/icons';
import { NoRecordFound } from '../../components/Shared';
import { StackedJob, StackDetailCard, StackedDeliveryTimeline } from '../../components/Stack';
import { StackDetail } from '../../types/Stack';
import { StackedJob as StackedJobType } from '../../types/Job';
import { STACK_MAP_OPERATION } from '../../mappers/stack.mapper';
import StackController from '../../controllers/stack.controller';

const StackDetailContainer: React.FC = (): React.ReactElement => {
    const params: { stackId: string } = useParams();
    const [stackData, setStackData] = useState<StackDetail>();

    const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
    const getStackById = async (): Promise<void> => {
        try {
            setIsLoadingData(true);
            const stack: StackDetail = await StackController.getStackById(params.stackId, STACK_MAP_OPERATION.DETAILED);
            setStackData(stack);
        } catch (error) {
            setStackData(undefined);
        } finally {
            setIsLoadingData(false);
        }
    };

    useEffect(() => {
        getStackById();
    }, [params.stackId]);

    return (
        <>
            <Breadcrumb className="mb-3">
                <Breadcrumb.Item>
                    <BlockOutlined className="d-inline-flex mr-1" />
                    <Link to="/admin/stacks" title="Stacks">
                        Stacks
                    </Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>Stack details</Breadcrumb.Item>
            </Breadcrumb>
            <Row gutter={[24, 16]}>
                {stackData ? (
                    <>
                        <Col span={8} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 8 }}>
                            <StackDetailCard
                                isLoadingData={isLoadingData}
                                stack={{
                                    id: stackData.id,
                                    apiJobId: stackData.apiJobId,
                                    createdAt: stackData.createdAt,
                                    status: stackData.status,
                                    client: stackData.client,
                                }}
                            />
                            {isLoadingData ? (
                                <Skeleton active />
                            ) : (
                                <div className="shadow-sm py-2 w-100 mt-3">
                                    <PageHeader
                                        className="site-page-header py-2"
                                        title={<p className="h5 m-0">Stacked Jobs</p>}
                                        extra={[
                                            <p key="status" className="text-right">
                                                <small>
                                                    <strong>N&deg; jobs:</strong>
                                                </small>
                                                <br />
                                                <span className="text-secondary">{stackData.jobs.length}</span>
                                            </p>,
                                        ]}
                                    />
                                    <div className="px-4">
                                        {stackData.jobs.map((job) => (
                                            <div className="mb-3" key={job.id}>
                                                <StackedJob job={job as Omit<StackedJobType, 'deliveries'>} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Col>
                        <Col span={16} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 16 }}>
                            <StackedDeliveryTimeline
                                isLoadingData={isLoadingData}
                                pickups={stackData.pickups}
                                dropoffs={stackData.dropoffs}
                            />
                        </Col>
                    </>
                ) : (
                    <Col span={24} className="shadow-sm">
                        <NoRecordFound message="No stack found." />
                    </Col>
                )}
            </Row>
        </>
    );
};

export default StackDetailContainer;
